import { useAuth } from './use-auth';

// Type for role strings
type UserRole = 'customer' | 'admin' | 'staff' | 'super_admin';

export function useRolePermissions() {
  const { user } = useAuth();

  // Map roleId to role string
  const getUserRole = (): UserRole | null => {
    if (!user) return null;
    switch (user.roleId) {
      case 2: // admin
        return 'admin';
      case 3: // staff
        return 'staff';
      case 1: // customer
        return 'customer';
      default:
        return 'customer';
    }
  };

  const userRole = getUserRole();

  // Helper to check if user has a specific role
  const hasRole = (role: UserRole): boolean => {
    return userRole === role;
  };

  // Helper to check permissions (simplified - can be expanded)
  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    // For now, admins have all permissions
    if (user.roleId === 2) return true;
    // Add more permission logic as needed
    return false;
  };

  const canAccess = (requiredRole: UserRole | UserRole[]): boolean => {
    if (!user || !userRole) return false;
    
    if (Array.isArray(requiredRole)) {
      return requiredRole.includes(userRole);
    }
    
    return userRole === requiredRole;
  };

  const canManage = (resource: string): boolean => {
    if (!user || !userRole) return false;
    
    switch (resource) {
      case 'users':
        return hasRole('admin') || hasRole('super_admin');
      case 'system':
        return hasRole('super_admin');
      case 'bookings':
        return hasRole('admin') || hasRole('super_admin') || hasRole('staff');
      case 'payments':
        return hasRole('admin') || hasRole('super_admin');
      case 'reports':
        return hasRole('admin') || hasRole('super_admin');
      case 'settings':
        return hasRole('admin') || hasRole('super_admin');
      default:
        return false;
    }
  };

  const canView = (resource: string): boolean => {
    if (!user || !userRole) return false;
    
    switch (resource) {
      case 'dashboard':
        return true; // All authenticated users can view dashboard
      case 'bookings':
        return hasRole('admin') || hasRole('super_admin') || hasRole('staff');
      case 'guests':
        return hasRole('admin') || hasRole('super_admin') || hasRole('staff');
      case 'housekeeping':
        return hasRole('admin') || hasRole('super_admin') || hasRole('staff');
      case 'maintenance':
        return hasRole('admin') || hasRole('super_admin') || hasRole('staff');
      case 'support':
        return hasRole('admin') || hasRole('super_admin') || hasRole('staff');
      case 'notifications':
        return hasRole('admin') || hasRole('super_admin') || hasRole('staff');
      case 'payments':
        return hasRole('admin') || hasRole('super_admin');
      default:
        return hasRole('admin') || hasRole('super_admin');
    }
  };

  const getRoleDisplayName = (role?: UserRole): string => {
    if (!role) return 'Unknown';
    
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'admin':
        return 'Admin';
      case 'staff':
        return 'Staff';
      case 'customer':
        return 'Customer';
      default:
        return 'Unknown';
    }
  };

  const getRoleColor = (role?: UserRole): string => {
    if (!role) return 'bg-gray-500';
    
    switch (role) {
      case 'super_admin':
        return 'bg-red-500';
      case 'admin':
        return 'bg-blue-500';
      case 'staff':
        return 'bg-green-500';
      case 'customer':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };

  return {
    user,
    canAccess,
    canManage,
    canView,
    hasPermission,
    hasRole,
    getRoleDisplayName,
    getRoleColor,
    isSuperAdmin: userRole === 'super_admin',
    isAdmin: userRole === 'admin',
    isStaff: userRole === 'staff',
    isCustomer: userRole === 'customer',
  };
}
